import { createReducer } from '@reduxjs/toolkit';
import {
  createAiCharacterObjection,
  createAiLearnerGoal,
  deleteAiCharacterObjection,
  deleteAiLearnerGoal,
  getAiRolePlaySubmission,
  setAiRolePlaySubmissionCompleted,
  setAiRolePlaySubmissionClosingReason,
} from 'redux/actions/ai-role-play';
import { ClosingReason } from 'lecture_pages/components/ai-role-play/ai-conversation-modal/stream-ai-response';
import { initialRootState } from '.';

/**
 * Creates a default submission object with null fields.
 * Used as a fallback when a submission doesn't exist in state yet.
 */
const createEmptySubmission = (
  id: number,
  aiRolePlayId: number,
  overrides: Partial<{
    finishedAt: string | null;
    closingReason: ClosingReason | null;
  }> = {},
) => ({
  id,
  finishedAt: null,
  closingReason: null,
  status: null,
  attemptNumber: null,
  createdAt: null,
  updatedAt: null,
  openingNarrative: null,
  leaderboardRank: null,
  leaderboardPoints: null,
  priorLeaderboardRank: null,
  feedback: null,
  aiRolePlay: { id: aiRolePlayId },
  ...overrides,
});

/**
 * Updates pointsReceived only if the new value is greater than the existing one.
 * This prevents failed retries (which return 0) from overwriting earned points.
 */
const updatePointsIfHigher = (
  aiRolePlay: { pointsReceived: number | null },
  newPoints: number | null,
) => {
  if ((newPoints ?? 0) > (aiRolePlay.pointsReceived ?? 0)) {
    aiRolePlay.pointsReceived = newPoints;
  }
};

export default createReducer(initialRootState, builder => {
  builder
    .addCase(createAiCharacterObjection.fulfilled, (state, action) => {
      const { aiRolePlayId, id, text } = action.payload;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.aiCharacterObjections = [
          ...aiRolePlay.aiCharacterObjections,
          { id, text },
        ];
      }
    })
    .addCase(deleteAiCharacterObjection.fulfilled, (state, action) => {
      const { aiCharacterObjectionId, aiRolePlayId } = action.meta.arg;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.aiCharacterObjections = aiRolePlay.aiCharacterObjections.filter(
          objection => objection.id !== aiCharacterObjectionId,
        );
      }
    })
    .addCase(createAiLearnerGoal.fulfilled, (state, action) => {
      const { aiRolePlayId, id, text } = action.payload;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.aiLearnerGoals = [
          ...aiRolePlay.aiLearnerGoals,
          { id, text },
        ];
      }
    })
    .addCase(deleteAiLearnerGoal.fulfilled, (state, action) => {
      const { aiLearnerGoalId, aiRolePlayId } = action.meta.arg;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.aiLearnerGoals = aiRolePlay.aiLearnerGoals.filter(
          goal => goal.id !== aiLearnerGoalId,
        );
      }
    })
    .addCase(getAiRolePlaySubmission.fulfilled, (state, action) => {
      const submission = action.payload.result;
      const aiRolePlay = state.models.aiRolePlays[submission.aiRolePlay.id];
      if (aiRolePlay) {
        state.models.aiRolePlaySubmissions[submission.id] = { ...state.models.aiRolePlaySubmissions[submission.id], ...submission };
        aiRolePlay.submissionId = submission.id;
        updatePointsIfHigher(aiRolePlay, submission.pointsReceived);
      }
    })
    .addCase(setAiRolePlaySubmissionCompleted, (state, action) => {
      const { aiRolePlayId, aiRolePlaySubmissionId, pointsReceived, finishedAt } = action.payload;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.submissionId = aiRolePlaySubmissionId;
        updatePointsIfHigher(aiRolePlay, pointsReceived);
        aiRolePlay.progress = 'completed';
      }
      const submission = state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId];
      if (submission) {
        submission.finishedAt = finishedAt;
      } else {
        state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId] = createEmptySubmission(
          aiRolePlaySubmissionId,
          aiRolePlayId,
          { finishedAt },
        );
      }
    })
    .addCase(setAiRolePlaySubmissionClosingReason, (state, action) => {
      const { aiRolePlaySubmissionId, closingReason, aiRolePlayId } = action.payload;
      const aiRolePlay = state.models.aiRolePlays[aiRolePlayId];
      if (aiRolePlay) {
        aiRolePlay.submissionId = aiRolePlaySubmissionId;
      }
      const submission = state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId];
      if (submission) {
        submission.closingReason = closingReason;
      } else {
        state.models.aiRolePlaySubmissions[aiRolePlaySubmissionId] = createEmptySubmission(
          aiRolePlaySubmissionId,
          aiRolePlayId,
          { closingReason },
        );
      }
    });
});
